import { Share } from '@mui/icons-material'
import { styled, Tooltip, IconButton } from '@mui/material'
import { useParams } from 'react-router-dom'
import { BucketType, BucketUserRole } from '../../../../../graphql/generated'
import { modalVar } from '../../../../../providers/apollo/cache'
import useSelectedMedia from '../../../hooks/useSelectedMedia'
import useBucketPlan from '../../../hooks/useBucketPlan'
import useProfilePlan from '../../../hooks/useProfilePlan'
import useBucket from '../../../hooks/useBucket'

const PillButton = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: '#fff',
  fontWeight: 500,
  fontSize: '0.875rem',
  cursor: 'pointer',
  gap: 8
})

export default function MediaSharePillButton() {
  const { restrictions, tooltipTitle } = useBucketPlan()
  const { isSharingEnabled } = useProfilePlan()
  const { selectedMedia } = useSelectedMedia()
  const { bucket, bucketUser } = useBucket()
  const { bucketId } = useParams<{ bucketId: string }>()
  const mediaIds: string[] = Array.from(selectedMedia.keys())

  const handleClick = () => {
    if (!bucketId || !mediaIds.length) return
    modalVar('bucketSharingLinkCreate')
  }

  const isPersonalBucket = bucket?.bucketType === BucketType.Mybucket
  const isGroupBucket = bucket?.bucketType === BucketType.Group
  const isViewer = bucketUser?.userRole === BucketUserRole.Viewer
  const hasMediaSelected = mediaIds.length > 0

  const canShare = () => {
    if (!hasMediaSelected || restrictions.sharing || (isGroupBucket && isViewer)) {
      return false
    }
    if (isPersonalBucket) {
      return isSharingEnabled
    }
    return true
  }

  const getTooltipTitle = () => {
    if (!hasMediaSelected) {
      return 'No Media Selected'
    }
    if (restrictions.sharing || (isGroupBucket && isViewer) || (isPersonalBucket && !isSharingEnabled)) {
      return tooltipTitle('sharing')
    }
    return 'Share Media'
  }

  const disabled = !canShare()

  if (disabled) {
    return null
  }

  return (
    <Tooltip title={getTooltipTitle()} arrow>
      <PillButton as="span">
        <IconButton
          color="inherit"
          aria-label="Share Media"
          disabled={disabled}
          onClick={handleClick}
          id="share-media-pill"
          data-testid="share-media-pill"
          sx={{ color: '#fff' }}
        >
          <Share />
        </IconButton>
        <span style={{ color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#fff' }}>Share</span>
      </PillButton>
    </Tooltip>
  )
}
